const STORAGE_KEY = "icraftx.deviceInstallId";

/**
 * Header-safe install id (Safari Headers.set throws
 * "The string did not match the expected pattern" for non-ByteString / CR/LF).
 * plan/28 device binding.
 */
export function isValidDeviceInstallId(raw: string | null | undefined): boolean {
  if (!raw) return false;
  if (raw.length < 8 || raw.length > 128) return false;
  // ASCII token-ish — no spaces, quotes, or high Unicode (Safari ByteString).
  return /^[\x21-\x7E]+$/.test(raw) && !/[\x7F\r\n]/.test(raw);
}

function mintDeviceInstallId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Stable per-browser install id for POS device binding — plan/28 */
export function getOrCreateDeviceInstallId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY)?.trim() ?? "";
    if (isValidDeviceInstallId(existing)) return existing;
    const id = mintDeviceInstallId();
    localStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    return `dev-session-${Date.now()}`;
  }
}

/** Drop a corrupt/stale id so the next call mints a header-safe one. */
export function rotateDeviceInstallId(): string {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  return getOrCreateDeviceInstallId();
}

export function deviceInstallHeaders(): Record<string, string> {
  const id = getOrCreateDeviceInstallId();
  // Defense: never hand Safari a value that fails ByteString header checks.
  if (!isValidDeviceInstallId(id)) {
    return { "X-Device-Install-Id": rotateDeviceInstallId() };
  }
  return { "X-Device-Install-Id": id };
}
